import React, { useState, useEffect } from 'react';
import {
  Search,
  Plus,
  Eye,
  Edit,
  Building2,
  MapPin,
  Phone,
  Mail,
  Users,
  TrendingUp,
  Target,
  Filter,
  Download,
  CheckCircle,
  AlertTriangle,
  Clock,
  Settings
} from 'lucide-react';
import { Branch } from '../../types';
import { supabase } from '../../lib/supabase';
import AddBranchModal from '../Modals/AddBranchModal';
import AddUnitModal from '../Modals/AddUnitModal';
import BranchReportModal from '../Modals/BranchReportModal';
import EmployeeReportModal from '../Modals/EmployeeReportModal';
import BranchDetailModal from '../Modals/BranchDetailModal';

const Branches: React.FC = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [regionFilter, setRegionFilter] = useState<string>('all');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [branches, setBranches] = useState<Branch[]>([]);
  const [selectedBranch, setSelectedBranch] = useState<Branch | null>(null);
  const [editingBranch, setEditingBranch] = useState<Branch | null>(null);
  const [showBranchReportModal, setShowBranchReportModal] = useState(false);
  const [showAddBranchModal, setShowAddBranchModal] = useState(false);
  const [showAddUnitModal, setShowAddUnitModal] = useState(false);
  const [showEmployeeReportModal, setShowEmployeeReportModal] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false); // Kept to avoid breaking existing logic if other components rely on it, though unused in render

  useEffect(() => {
    fetchBranches();
  }, []);

  const fetchBranches = async () => {
    try {
      setLoading(true);
      const { data: branchesData, error: branchesError } = await supabase
        .from('branches')
        .select('*');

      if (branchesError) throw branchesError;

      const validBranchIds = new Set((branchesData || []).map((b: any) => b.id));
      const UNKNOWN_BRANCH_ID = 'unknown_branch';

      let allLeads: any[] = [];
      let page = 0;
      const pageSize = 1000;
      let hasMore = true;

      while (hasMore) {
        const { data: leadsBatch, error: leadsError } = await supabase
          .from('leads')
          .select('id, branch_id, status, is_registered, tracking_status, created_at')
          .range(page * pageSize, (page + 1) * pageSize - 1);

        if (leadsError) throw leadsError;

        if (leadsBatch && leadsBatch.length > 0) {
          allLeads = [...allLeads, ...leadsBatch];
          if (leadsBatch.length < pageSize) hasMore = false;
          page++;
        } else {
          hasMore = false;
        }
      }

      const currentMonth = new Date().getMonth();
      const currentYear = new Date().getFullYear();
      const startOfMonth = new Date(currentYear, currentMonth, 1).toISOString();

      let allActivities: any[] = [];
      page = 0;
      hasMore = true;

      while (hasMore) {
        const { data: activitiesBatch, error: activitiesError } = await supabase
          .from('lead_activities')
          .select('lead_id, metadata, created_at, activity_type')
          .in('activity_type', ['status_changed', 'updated'])
          .gte('created_at', startOfMonth)
          .range(page * pageSize, (page + 1) * pageSize - 1);

        if (activitiesError) throw activitiesError;

        if (activitiesBatch && activitiesBatch.length > 0) {
          allActivities = [...allActivities, ...activitiesBatch];
          if (activitiesBatch.length < pageSize) hasMore = false;
          page++;
        } else {
          hasMore = false;
        }
      }

      const enrolledThisMonthIds = new Set<string>();
      allActivities.forEach((act: any) => {
        const isEnrolledCheck =
          (act.activity_type === 'status_changed' && act.metadata?.status?.new === 'enrolled') ||
          (act.activity_type === 'updated' && act.metadata?.is_registered?.new === true);

        if (isEnrolledCheck) {
          enrolledThisMonthIds.add(act.lead_id);
        }
      });

      const totalLeadsCounts: Record<string, number> = {};
      const thisMonthEnrolledCounts: Record<string, number> = {};
      const thisMonthLeadsCounts: Record<string, number> = {};
      let hasUnknownBranchData = false;

      (allLeads || []).forEach((l: any) => {
        let branchId = l.branch_id;
        if (!branchId || !validBranchIds.has(branchId)) branchId = UNKNOWN_BRANCH_ID;

        // Total Leads Count
        totalLeadsCounts[branchId] = (totalLeadsCounts[branchId] || 0) + 1;
        if (branchId === UNKNOWN_BRANCH_ID) hasUnknownBranchData = true;

        // This Month Leads Count
        const createdThisMonth = new Date(l.created_at) >= new Date(startOfMonth);
        if (createdThisMonth) {
          thisMonthLeadsCounts[branchId] = (thisMonthLeadsCounts[branchId] || 0) + 1;
        }

        const isEnrolled =
          l.status === 'enrolled' ||
          l.is_registered === true ||
          l.tracking_status === 'enrolled';

        if (isEnrolled) {
          // This Month Registrations Count
          if (enrolledThisMonthIds.has(l.id) || createdThisMonth) {
            thisMonthEnrolledCounts[branchId] = (thisMonthEnrolledCounts[branchId] || 0) + 1;
          }
        }
      });

      const colors = ['#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#EC4899', '#6366F1', '#14B8A6'];

      let mappedBranches = (branchesData || []).map((b: any, index: number) => ({
        id: b.id,
        name: b.name,
        code: b.code || '',
        city: b.city || '',
        region: b.region || 'Genel',
        contactPerson: b.contact_person || 'Belirtilmemiş',
        email: b.email || '',
        phone: b.phone || '',
        address: b.address || '',
        createdAt: b.created_at || new Date().toISOString(),
        isActive: b.is_active !== false,
        totalStudents: totalLeadsCounts[b.id] || 0,
        monthlyTarget: b.monthly_target || 50,
        currentMonthRegistrations: thisMonthEnrolledCounts[b.id] || 0,
        currentMonthLeads: thisMonthLeadsCounts[b.id] || 0,
        units: [],
        color: colors[index % colors.length]
      }));

      // Add Unknown Branch if it has data
      if (hasUnknownBranchData) {
        mappedBranches.push({
          id: UNKNOWN_BRANCH_ID,
          name: 'Atanmamış Şube',
          code: 'N/A',
          city: '-',
          region: 'Bilinmiyor',
          contactPerson: '-',
          email: '-',
          phone: '-',
          address: '-',
          createdAt: new Date().toISOString(),
          isActive: true,
          totalStudents: totalLeadsCounts[UNKNOWN_BRANCH_ID] || 0,
          monthlyTarget: 0,
          currentMonthRegistrations: thisMonthEnrolledCounts[UNKNOWN_BRANCH_ID] || 0,
          currentMonthLeads: thisMonthLeadsCounts[UNKNOWN_BRANCH_ID] || 0,
          units: [],
          color: '#9CA3AF'
        });
      }

      setBranches(mappedBranches);

    } catch (err) {
      console.error('Branches fetch error:', err);
    } finally {
      setLoading(false);
    }
  };

  const filteredBranches = branches.filter(branch => {
    const matchesSearch =
      branch.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      branch.city.toLowerCase().includes(searchTerm.toLowerCase()) ||
      branch.contactPerson.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesRegion = regionFilter === 'all' || branch.region === regionFilter;
    const matchesStatus = statusFilter === 'all' ||
      (statusFilter === 'active' && branch.isActive) ||
      (statusFilter === 'inactive' && !branch.isActive);

    return matchesSearch && matchesRegion && matchesStatus;
  });

  const handleBranchClick = (branch: Branch) => {
    setSelectedBranch(branch);
    setShowModal(true);
  };

  const handleSaveBranch = () => {
    fetchBranches();
    setShowAddBranchModal(false);
    setEditingBranch(null);
  };

  const handleSaveUnit = () => {
    setShowAddUnitModal(false);
  };

  const handleViewEmployee = (employee: any) => {
    setSelectedEmployee(employee);
    setShowEmployeeReportModal(true);
  };

  const getPerformanceColor = (current: number, target: number) => {
    const percentage = (current / target) * 100;
    if (percentage >= 90) return 'text-green-600 dark:text-green-400';
    if (percentage >= 70) return 'text-yellow-600 dark:text-yellow-400';
    return 'text-red-600 dark:text-red-400';
  };

  const getPerformanceIcon = (current: number, target: number) => {
    const percentage = (current / target) * 100;
    if (percentage >= 90) return <CheckCircle className="w-5 h-5 text-green-500" />;
    if (percentage >= 70) return <Clock className="w-5 h-5 text-yellow-500" />;
    return <AlertTriangle className="w-5 h-5 text-red-500" />;
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-4 sm:space-y-0">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Şubeler</h2>
          <p className="text-gray-600 dark:text-gray-400">Şube ağınızı yönetin ve performanslarını takip edin</p>
        </div>
        <div className="flex items-center space-x-3">
          <button className="flex items-center space-x-2 bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg transition-colors">
            <Download className="w-4 h-4" />
            <span>Rapor İndir</span>
          </button>
          <button
            onClick={() => setShowAddBranchModal(true)}
            className="flex items-center space-x-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg transition-colors"
          >
            <Plus className="w-4 h-4" />
            <span>Yeni Şube</span>
          </button>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      ) : (
        <>
          {/* Stats Overview */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-xl p-6">
              <div className="flex items-center space-x-3">
                <Building2 className="w-8 h-8 text-blue-500" />
                <div>
                  <p className="text-2xl font-bold text-gray-900 dark:text-white">{branches.length}</p>
                  <p className="text-sm text-gray-600 dark:text-gray-400">Aktif Şube</p>
                </div>
              </div>
            </div>
            <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-xl p-6">
              <div className="flex items-center space-x-3">
                <Users className="w-8 h-8 text-green-500" />
                <div>
                  <p className="text-2xl font-bold text-gray-900 dark:text-white">
                    {branches.reduce((sum, branch) => sum + branch.totalStudents, 0).toLocaleString()}
                  </p>
                  <p className="text-sm text-gray-600 dark:text-gray-400">Toplam Öğrenci</p>
                </div>
              </div>
            </div>
            <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-xl p-6">
              <div className="flex items-center space-x-3">
                <Target className="w-8 h-8 text-purple-500" />
                <div>
                  <p className="text-2xl font-bold text-gray-900 dark:text-white">
                    %{Math.round(branches.reduce((acc, b) => acc + (b.totalStudents / (b.monthlyTarget || 1) * 100), 0) / (branches.length || 1))}
                  </p>
                  <p className="text-sm text-gray-600 dark:text-gray-400">Ortalama Hedef Başarı</p>
                </div>
              </div>
            </div>
            <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-xl p-6">
              <div className="flex items-center space-x-3">
                <TrendingUp className="w-8 h-8 text-yellow-500" />
                <div>
                  <p className="text-2xl font-bold text-gray-900 dark:text-white">+15%</p>
                  <p className="text-sm text-gray-600 dark:text-gray-400">Bu Ay Artış</p>
                </div>
              </div>
            </div>
          </div>

          {/* Filters */}
          <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-xl p-6">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                <input
                  type="text"
                  placeholder="Şube ara..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2.5 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              <select
                value={regionFilter}
                onChange={(e) => setRegionFilter(e.target.value)}
                className="bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg px-4 py-2.5 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="all">Tüm Bölgeler</option>
                <option value="Marmara">Marmara</option>
                <option value="İç Anadolu">İç Anadolu</option>
                <option value="Ege">Ege</option>
                <option value="Akdeniz">Akdeniz</option>
                <option value="Karadeniz">Karadeniz</option>
              </select>

              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg px-4 py-2.5 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="all">Tüm Durumlar</option>
                <option value="active">Aktif</option>
                <option value="inactive">Pasif</option>
              </select>

              <button className="flex items-center justify-center space-x-2 bg-gray-100 hover:bg-gray-200 dark:bg-gray-800 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300 px-4 py-2.5 rounded-lg transition-colors">
                <Filter className="w-4 h-4" />
                <span>Filtrele</span>
              </button>
            </div>
          </div>

          {/* Branches Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredBranches.map((branch) => {
              const performancePercentage = (branch.currentMonthRegistrations / branch.monthlyTarget) * 100;

              return (
                <div
                  key={branch.id}
                  onClick={() => handleBranchClick(branch)}
                  className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-xl p-6 hover:shadow-lg transition-all duration-200 hover:scale-[1.02] cursor-pointer"
                >
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center space-x-3">
                      <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-purple-500 rounded-xl flex items-center justify-center">
                        <Building2 className="w-6 h-6 text-white" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-gray-900 dark:text-white">{branch.name}</h3>
                        <p className="text-sm text-gray-600 dark:text-gray-400">{branch.code}</p>
                      </div>
                    </div>
                    <div className="flex items-center space-x-2">
                      {getPerformanceIcon(branch.currentMonthRegistrations, branch.monthlyTarget)}
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${branch.isActive
                        ? 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400'
                        : 'bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-400'
                        }`}>
                        {branch.isActive ? 'Aktif' : 'Pasif'}
                      </span>
                    </div>
                  </div>

                  <div className="space-y-3 mb-4">
                    <div className="flex items-center space-x-2 text-sm text-gray-600 dark:text-gray-400">
                      <MapPin className="w-4 h-4" />
                      <span>{branch.city}, {branch.region}</span>
                    </div>
                    <div className="flex items-center space-x-2 text-sm text-gray-600 dark:text-gray-400">
                      <Phone className="w-4 h-4" />
                      <span>{branch.phone}</span>
                    </div>
                    <div className="flex items-center space-x-2 text-sm text-gray-600 dark:text-gray-400">
                      <Mail className="w-4 h-4" />
                      <span className="truncate">{branch.email}</span>
                    </div>
                    <div className="flex items-center space-x-2 text-sm text-gray-600 dark:text-gray-400">
                      <Users className="w-4 h-4" />
                      <span>Yönetici: {branch.contactPerson}</span>
                    </div>
                  </div>

                  {/* Statistics */}
                  <div className="grid grid-cols-3 gap-2 mb-4">
                    <div className="text-center">
                      <p className="text-xl font-bold text-gray-900 dark:text-white">{branch.totalStudents}</p>
                      <p className="text-[10px] text-gray-600 dark:text-gray-400">Toplam Lead</p>
                    </div>
                    <div className="text-center border-l border-r border-gray-200 dark:border-gray-700">
                      <p className="text-xl font-bold text-gray-900 dark:text-white">{branch.currentMonthLeads}</p>
                      <p className="text-[10px] text-gray-600 dark:text-gray-400">Aylık Lead</p>
                    </div>
                    <div className="text-center">
                      <p className="text-xl font-bold text-gray-900 dark:text-white">{branch.currentMonthRegistrations}</p>
                      <p className="text-[10px] text-gray-600 dark:text-gray-400">Bu Ay Kayıt</p>
                    </div>
                  </div>

                  <div className="mb-4">
                    <div className="flex justify-between text-sm mb-1">
                      <span className="text-gray-600 dark:text-gray-400">Aylık Hedef</span>
                      <span className={`font-medium ${getPerformanceColor(branch.currentMonthRegistrations, branch.monthlyTarget)}`}>
                        {branch.currentMonthRegistrations}/{branch.monthlyTarget} (%{Math.round(performancePercentage) || 0})
                      </span>
                    </div>
                    <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                      <div
                        className={`h-2 rounded-full transition-all duration-500 ${performancePercentage >= 90 ? 'bg-green-500' :
                          performancePercentage >= 70 ? 'bg-yellow-500' : 'bg-red-500'
                          }`}
                        style={{ width: `${Math.min(performancePercentage || 0, 100)}%` }}
                      ></div>
                    </div>
                  </div>

                  <div className="pt-4 border-t border-gray-100 dark:border-gray-700 flex justify-between items-center">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        // For now just select, we might add a modal later
                        setSelectedBranch(branch);
                        setShowModal(true);
                      }}
                      className="px-3 py-1.5 text-sm font-medium text-indigo-600 bg-indigo-50 hover:bg-indigo-100 rounded-md transition-colors dark:bg-indigo-900/30 dark:text-indigo-400 dark:hover:bg-indigo-900/50 flex items-center"
                    >
                      <Eye className="w-4 h-4 mr-1" />
                      Detay
                    </button>
                    <div className="flex space-x-2">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setEditingBranch(branch);
                          setShowAddBranchModal(true);
                        }}
                        className="px-3 py-1.5 text-sm font-medium text-emerald-600 bg-emerald-50 hover:bg-emerald-100 rounded-md transition-colors dark:bg-emerald-900/30 dark:text-emerald-400 dark:hover:bg-emerald-900/50 flex items-center"
                      >
                        <Edit className="w-4 h-4 mr-1" />
                        Düzenle
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedBranch(branch);
                          setShowBranchReportModal(true);
                        }}
                        className="p-1.5 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                        title="Şube Raporu"
                      >
                        <Settings className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Modals */}
          {showModal && selectedBranch && (
            <BranchDetailModal
              isOpen={showModal}
              onClose={() => {
                setShowModal(false);
                setSelectedBranch(null);
              }}
              branch={selectedBranch}
              onViewEmployee={handleViewEmployee}
            />
          )}

          {showAddBranchModal && (
            <AddBranchModal
              isOpen={showAddBranchModal}
              onClose={() => {
                setShowAddBranchModal(false);
                setEditingBranch(null);
              }}
              onSave={handleSaveBranch}
              initialData={editingBranch}
            />
          )}

          {showAddUnitModal && selectedBranch && (
            <AddUnitModal
              isOpen={showAddUnitModal}
              onClose={() => setShowAddUnitModal(false)}
              onSave={handleSaveUnit}
              branchId={selectedBranch.id}
            />
          )}

          {showEmployeeReportModal && selectedEmployee && (
            <EmployeeReportModal
              isOpen={showEmployeeReportModal}
              onClose={() => {
                setShowEmployeeReportModal(false);
                setSelectedEmployee(null);
              }}
              employee={selectedEmployee} // Fixed prop name from employeeId/employeeName
            />
          )}

          {showBranchReportModal && selectedBranch && (
            <BranchReportModal
              isOpen={showBranchReportModal}
              onClose={() => {
                setShowBranchReportModal(false);
                setSelectedBranch(null);
              }}
              branch={selectedBranch}
            />
          )}
        </>
      )}
    </div>
  );
};

export default Branches;